import React from 'react';
import { MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomSelectAndChildren } from '../../../../components/CustomSelectAndChildren';
import { CustomSelectMultiple } from '../../../../components/CustomSelectMultiple';
import { CustomTextField } from '../../../../components/CustomTextField';
import { CustomDatePicker } from '../../../../components/CustomDatePicker';
import { fieldNameEnum } from '../../../../enums/fieldNameEnum';
import { NULL_OPERATIONS } from '../Model/Model';
import {PERIOD_TIME_UNIT_ENUM} from "../../../../enums/PeriodTimeUnitEnum";
import {DATA_TYPE_MODEL, getFieldNameByValue, getFieldValueTrans} from "../../Model/Model";
/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
const RuleValueInput = ({
    rule,
    onValueChange,
    onDateChange,
    onFromDateChange,
    onToDateChange,
    onFromValueChange,
    onToValueChange,
    onAssignMultiple,
    onValidityChange,
}) => {
    const { t } = useTranslation();
    const disabled = !rule.field || !rule.condition || NULL_OPERATIONS.includes(rule.operationKey);

    if (!rule.condition || NULL_OPERATIONS.includes(rule.operationKey)) return null;

    if (rule.inputTransferableList) {
        const allOptions = [...rule.availablesList, ...rule.assignedList]
            .sort((a, b) => String(a.id).localeCompare(String(b.id)));
        const selectedIds = rule.assignedList.map(i => i.id);
        return (
            <CustomSelectMultiple
                id={`slt_sf_multi_${rule.id}`}
                name="assignedList"
                trans="value"
                list={allOptions}
                value={selectedIds}
                keyName="id"
                required={true}
                emptyFields={rule.emptyFields}
                marginBottom={false}
                onChange={(e) => onAssignMultiple(e.target.value)}
                placeholder={t("select_one_or_more_values")}
            />
        );
    }

    const renderDropdownOptions = () => {
        const safeValues = rule.values || [];
        if (rule.typeAttribute === DATA_TYPE_MODEL.BOOLEAN) {
            return safeValues.map((item, idx) => (
                <MenuItem
                    key={idx}
                    value={item.toLowerCase() === 'true' ? true : item.toLowerCase() === 'false' && false}
                >
                    {getFieldNameByValue(rule.fieldName)
                        ? t(fieldNameEnum[rule.fieldName][item.toLowerCase()]).toUpperCase()
                        : t(item.toLowerCase()).toUpperCase()}
                </MenuItem>
            ));
        }
        if (rule.typeAttribute === DATA_TYPE_MODEL.UUID) {
            return safeValues.map((item, idx) => (
                <MenuItem key={idx} value={item.id}>{item.name}</MenuItem>
            ));
        }
        return safeValues.map((item, idx) => {
            // A value without a translation is read as the service sends it
            const trans = getFieldNameByValue(rule.fieldName)
                ? getFieldValueTrans(fieldNameEnum, rule.fieldName, item)
                : null;
            return (
                <MenuItem key={idx} value={item}>
                    {trans ? t(trans).toUpperCase() : item}
                </MenuItem>
            );
        });
    };

    if (rule.inputDropdown) {
        return (
            <CustomSelectAndChildren
                id={`slt_sf_val_${rule.id}`}
                name="value"
                trans="value"
                value={rule.valueAttribute}
                onChange={(e) => onValueChange(e.target.value)}
                required={true}
                emptyFields={rule.emptyFields}
                showNoSelectionOption={false}
                marginBottom={false}
            >
                {renderDropdownOptions()}
            </CustomSelectAndChildren>
        );
    }

    if (rule.inputDate) {
        return (
            <CustomDatePicker
                id={`dp_sf_${rule.id}`}
                name="dateAttribute"
                disabled={false}
                onChange={onDateChange}
                trans="date"
                value={rule.dateAttribute}
                required={true}
                openTo="day"
                onError={(err) => onValidityChange('isValidDate', err === null)}
                isValid={rule.isValidDate}
                emptyFields={rule.emptyFields}
                noMarginBottom={true}
            />
        );
    }

    if (rule.inputTwoDates) {
        return (
            <div className="d-flex gap-8px">
                <CustomDatePicker
                    id={`dp_sf_from_${rule.id}`}
                    name="from"
                    disabled={false}
                    onChange={onFromDateChange}
                    trans="from"
                    value={rule.fromValue}
                    required={true}
                    openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                    onError={(err) => onValidityChange('isValidFrom', err === null)}
                    isValid={rule.isValidFrom}
                    emptyFields={rule.emptyFields && !rule.fromValue}
                    noMarginBottom={true}
                />
                <CustomDatePicker
                    id={`dp_sf_to_${rule.id}`}
                    name="to"
                    disabled={rule.fromValue === null}
                    onChange={onToDateChange}
                    trans="to"
                    value={rule.toValue}
                    required={true}
                    openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                    onError={(err) => onValidityChange('isValidTo', err === null)}
                    isValid={rule.isValidTo}
                    emptyFields={rule.emptyFields && !rule.toValue}
                    minDate={rule.fromValue}
                    noMarginBottom={true}
                />
            </div>
        );
    }

    if (rule.inputTwoNumbers) {
        return (
            <div className="d-flex gap-8px">
                <CustomTextField
                    id={`txt_sf_from_${rule.id}`}
                    name="fromValue"
                    value={rule.fromValue || ''}
                    trans="from"
                    type="number"
                    disabled={false}
                    required={true}
                    isValid={rule.isValidFrom}
                    emptyFields={rule.emptyFields && !rule.fromValue}
                    onChange={(e) => onFromValueChange(e.target.value)}
                    marginBottom={false}
                    length={9}
                />
                <CustomTextField
                    id={`txt_sf_to_${rule.id}`}
                    name="toValue"
                    value={rule.toValue || ''}
                    trans="to"
                    type="number"
                    disabled={false}
                    required={true}
                    isValid={rule.isValidTo}
                    isValidText={t("it_must_be_greater_than_or_equal_to", { min: rule.fromValue })}
                    emptyFields={rule.emptyFields && !rule.toValue}
                    onChange={(e) => onToValueChange(e.target.value)}
                    marginBottom={false}
                    length={9}
                />
            </div>
        );
    }

    return (
        <CustomTextField
            id={`txt_sf_val_${rule.id}`}
            name="valueAttribute"
            value={rule.valueAttribute}
            trans="value"
            type={rule.inputNumber ? 'number' : 'text'}
            disabled={disabled}
            required={true}
            emptyFields={rule.emptyFields && !disabled}
            onChange={(e) => onValueChange(e.target.value)}
            marginBottom={false}
            length={rule.inputNumber ? 9 : 60}
        />
    );
};

export default RuleValueInput;
